import { Button } from "../../components/ui/button";
import React, { useContext, useState } from "react";
import { AuthContext } from "../../contexts/Auth/Auth.Context";
import axios from "../../utils/axios";
import { REACT_APP_SESSION_DATA } from "../../constants";
import { ErrorAlert } from "../registration/ErrorAlert";
import { Loader2 } from "lucide-react"; // Import Eye and EyeOff icons

interface FormData {
  email: string;
  password: string;
}

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"form">) {
  const { dispatch } = useContext(AuthContext);

  const [formData, setFormData] = useState<FormData>({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorDescription, setErrorDescription] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setShowError(false);
  };

  const handleLogin = () => {
    setLoading(true);

    try {
      const body = {
        username: formData.email,
        password: formData.password,
      };

      axios
        .post("/users/login", body)
        .then(function(res){
                  if(res.status === 200){
                    const data = res.data;
                    console.log(data);
                    localStorage.setItem(REACT_APP_SESSION_DATA, data?.token);
                    dispatch({ type: "SIGN_IN", token: data?.token, data: data?.user });
                    setLoading(false);
                  }
          })
        .catch((error: any) => {
          console.log(error);
          setErrorDescription(error?.message);
          setLoading(false);
          setShowError(true);
          setTimeout(() => {
            setShowError(false);
          }, 60000);
        });
    } catch (error: any) {
      console.log(error);
      setErrorDescription(error?.message);
      setLoading(false);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 60000);
    }
  };

  return (
    <>
    <div className="min-h-screen flex fle-col bg-green-100 items-center justify-center">
    <div className="py-6 px-4">
      <div className="grid md:grid-cols-2 items-center gap-6 max-w-6xl w-full">
        <div className="border border-slate-300 rounded-lg p-6 max-w-md shadow-[0_2px_22px_-4px_rgba(93,96,127,0.2)] max-md:mx-auto">
          <form className="space-y-6">
            <div className="mb-12">
              <h3 className="text-slate-900 text-3xl font-semibold">Sign in</h3>
              <p className="text-slate-500 text-sm mt-6 leading-relaxed">Sign in to your account and explore a world of possibilities. Your journey begins here.</p>
            </div>
            {showError && <ErrorAlert errorDescription={errorDescription} />}
            <div>
              <label htmlFor="username" className="text-slate-800 text-sm font-medium mb-2 block">User name</label>
              <div className="relative flex items-center">
                <input
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="text"
                  required
                  className="w-full text-sm text-slate-800 border border-slate-300 pl-4 pr-10 py-3 rounded-lg outline-green-600"
                  placeholder="Enter user name"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 24 24">
                  <circle cx="10" cy="7" r="6" data-original="#000000"></circle>
                  <path d="M14 15H6a5 5 0 0 0-5 5 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 5 5 0 0 0-5-5zm8-4h-2.59l.3-.29a1 1 0 0 0-1.42-1.42l-2 2a1 1 0 0 0 0 1.42l2 2a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.42l-.3-.29H22a1 1 0 0 0 0-2z" data-original="#000000"></path>
                </svg>
              </div>
            </div>
            <div>
              <label htmlFor="password" className="text-slate-800 text-sm font-medium mb-2 block">Password</label>
              <div className="relative flex items-center">
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full text-sm text-slate-800 border border-slate-300 pl-4 pr-10 py-3 rounded-lg outline-green-600"
                  placeholder="Enter password"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4 cursor-pointer" viewBox="0 0 128 128">
                  <path d="M64 104C22.127 104 1.367 67.496.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24s62.633 36.504 63.496 38.057a4 4 0 0 1 0 3.887C126.633 67.496 105.873 104 64 104zM8.707 63.994C13.465 71.205 32.146 96 64 96c31.955 0 50.553-24.775 55.293-31.994C114.535 56.795 95.854 32 64 32 32.045 32 13.447 56.775 8.707 63.994zM64 88c-13.234 0-24-10.766-24-24s10.766-24 24-24 24 10.766 24 24-10.766 24-24 24zm0-40c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16z" data-original="#000000"></path>
                </svg>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-slate-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-3 block text-sm text-slate-500">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="jajvascript:void(0);" className="text-green-600 hover:underline font-medium">
                  Forgot your password?
                </a>
              </div>
            </div>

            <div className="mt-12!">
              <Button onClick={() => {handleLogin()}} type="button" className="w-full shadow-xl py-2.5 px-4 text-[15px] font-medium tracking-wide rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none">
                {loading ? <Loader2 className="animate-spin h-5 w-5 text-white" /> : "Sign in"}
              </Button>
              <p className="text-sm mt-6! text-center text-slate-500">
                Don&apos;t have an account? <a href="/accounts/register" className="text-green-600 font-medium hover:underline ml-1 whitespace-nowrap">Register here</a>
              </p>
            </div>
          </form>
        </div>

        <div className="max-md:mt-8">
          <img src="/map2.svg" className="w-full h-full mx-auto block object-cover" alt="login img" />
        </div>
      </div>
    </div>
  </div>
  </>
  );
}
